import { useSubscription } from '@apollo/client'
import { Flex } from '@chakra-ui/react'

import { isNotNullOrUndefined, joinWithConjunction } from '@/utils/helpers'
import type { UserPresenceKey } from '@/server/services/userPresence/types'

import { useUser } from '@/client/context/user'
import useForceUpdate from '@/client/hooks/useForceUpdate'
import useInterval from '@/client/hooks/useInterval'
import type {
  PostTypingStatusUserPresenceSubscription,
  PostTypingStatusUserPresenceSubscriptionVariables,
} from '@/client/services/hasura/generated/graphql'

import { SUBSCRIPTION_POST_TYPING_STATUS_USER_PRESENCE } from '@/components/Community/Post/PostTypingStatus.hasura'
import type { PostEventDetails } from '@/components/Community/Post/types'

const OPTIMISTIC_TYPING_THRESHOLD_SECONDS = 5

export default function PostTypingStatus({
  postId,
  postEvents,
}: {
  postId: number
  postEvents: PostEventDetails[]
}) {
  const user = useUser()
  const forceUpdate = useForceUpdate()

  useInterval(forceUpdate, (OPTIMISTIC_TYPING_THRESHOLD_SECONDS / 2) * 1000, [])

  const key: UserPresenceKey = `post:${postId}`
  const { data } = useSubscription<
    PostTypingStatusUserPresenceSubscription,
    PostTypingStatusUserPresenceSubscriptionVariables
  >(SUBSCRIPTION_POST_TYPING_STATUS_USER_PRESENCE, {
    variables: {
      key,
    },
  })

  const postEventsToLookAt = postEvents.slice(-10)

  const userNames = (data?.user_presence ?? [])
    // Exclude self
    .filter(presence => presence.user.id !== user.id)
    // Filter if they sent a message within the last few seconds or so
    .filter(
      presence =>
        !postEventsToLookAt.some(
          postEvent =>
            presence.user.id === postEvent.user.id &&
            Math.abs(postEvent.createdAt.diffNow().as('seconds')) <
              OPTIMISTIC_TYPING_THRESHOLD_SECONDS
        )
    )
    // Map to conversation_user names
    .map(presence => presence.user.virtual_display_name)
    .filter(isNotNullOrUndefined)

  if (userNames.length === 0) {
    return null
  }

  return (
    <Flex flexDir="row" justifyContent="left" p="8px" color="fg.subtle">
      {joinWithConjunction(userNames, 'and')} {userNames.length === 1 ? 'is' : 'are'} typing!
    </Flex>
  )
}
